import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { NotesViewPageStore } from "@/store/pages/NotesViewPageStore/NotesViewPageStore";
import { action, computed, observable } from "mobx";
import { makeObservable } from "mobx";
import { createRef, RefObject } from "react";

export class FindInNoteStore {
  readonly notesViewPageStore: NotesViewPageStore;

  isOpen = false;
  isReplaceMode = false;
  searchQuery = "";
  replacementText = "";
  currentMatchIndex = -1;
  matchesCount = 0;
  replaceableMatchesCount = 0;
  focusRequestId = 0;
  anchorRef: RefObject<HTMLButtonElement> = createRef();

  constructor(notesViewPageStore: NotesViewPageStore) {
    this.notesViewPageStore = notesViewPageStore;

    makeObservable(this, {
      notesViewPageStore: false,
      useEffects: false,

      anchorRef: observable,
      isOpen: observable,
      isReplaceMode: observable,
      searchQuery: observable,
      replacementText: observable,
      currentMatchIndex: observable,
      matchesCount: observable,
      replaceableMatchesCount: observable,
      focusRequestId: observable,
      canReplace: computed,
      open: action,
      close: action,
      toggleReplaceMode: action,
      setSearchQuery: action,
      setReplacementText: action,
      setSearchResults: action,
      findNext: action,
      findPrevious: action,
    });
  }

  open = () => {
    this.isOpen = true;
    this.focusRequestId += 1;
  };

  close = () => {
    this.isOpen = false;
    this.isReplaceMode = false;
    this.searchQuery = "";
    this.replacementText = "";
    this.currentMatchIndex = 0;
    this.matchesCount = 0;
    this.replaceableMatchesCount = 0;
  };

  toggleReplaceMode = () => {
    this.isReplaceMode = !this.isReplaceMode;
  };

  setSearchQuery = (searchQuery: string) => {
    if (searchQuery === this.searchQuery) return;

    this.searchQuery = searchQuery;
    this.currentMatchIndex = -1;
    this.matchesCount = 0;
    this.replaceableMatchesCount = 0;
  };

  setReplacementText = (replacementText: string) => {
    this.replacementText = replacementText;
  };

  setSearchResults = ({
    matchesCount,
    currentMatchIndex,
    replaceableMatchesCount = matchesCount,
  }: {
    matchesCount: number;
    currentMatchIndex: number;
    replaceableMatchesCount?: number;
  }) => {
    this.matchesCount = matchesCount;
    this.currentMatchIndex = currentMatchIndex;
    this.replaceableMatchesCount = replaceableMatchesCount;
  };

  get canReplace() {
    return (
      this.searchQuery.trim().length > 0 &&
      this.replacementText.length > 0 &&
      this.replaceableMatchesCount > 0
    );
  }

  findNext = () => {
    if (this.matchesCount === 0) return;
    this.currentMatchIndex = (this.currentMatchIndex + 1) % this.matchesCount;
  };

  findPrevious = () => {
    if (this.matchesCount === 0) return;
    this.currentMatchIndex = Math.abs(
      (this.currentMatchIndex - 1 + this.matchesCount) % this.matchesCount
    );
  };

  useEffects = () => {
    useEffectOnMount(() => {
      return () => this.close();
    });
  };
}
